import React, { useEffect } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '../components';
import { parse } from 'query-string';
import { LoadingPage } from '../components';
import { Progress } from '../components/Progress';
import styles from './EmployeeInput.scss';
import moment from 'moment';
import _ from 'lodash';
import { Redirect } from 'react-router';
import * as questionComponents from '../questions';
import QuestionGroup from '../components/QuestionGroup';
import { withProps, lifecycle } from 'recompose';
import { QuestionDescriptionsProvider } from '../components/QuestionDescription';
import ListGroup from 'jbc-front/components/ListGroup';
import { recordDisplay } from '../utils';
import { AdminUser, SingleEmployee } from 'jbc-front/components/icons';
import { Spouse } from './EmployeeInput/Spouse';
import { OtherFamilies } from './EmployeeInput/OtherFamilies';
import { LifeInsurances } from './EmployeeInput/LifeInsurances';
import { useFetchQuestionGroups } from 'features/questions/hooks/employee/useFetchQuestionGroups';
import { setTelemetryUser } from '@jbc-year-end-adj/common/telemetry';

const CLIENT_SETTINGS = gql`
  query clientSettings {
    request {
      id
      comment
      requestComments {
        id
        actedUser {
          id
          name
        }
        commentBy
        comment
        createdAt
      }
      employee {
        id
        status
        profile {
          id
          firstName
          lastName
        }
      }
      actedUser {
        id
        name
      }
      updatedAt
    }
    clientSetting {
      id
      submissionDeadline {
        id
        submissionDeadlineAt
      }
    }
  }
`;

const EmployeeInput = ({ location: { search } }) => {
  const { current } = parse(search);
  const { loading, data } = useFetchQuestionGroups();
  const { loading: clientSettingsLoading, data: clientSettingsData } = useQuery(CLIENT_SETTINGS, {
    fetchPolicy: 'network-only'
  });
  useEffect(() => {
    const employee = clientSettingsData?.request?.employee;
    if (employee) {
      setTelemetryUser({ id: `yea-employee${employee.id}` });
    }
  }, [clientSettingsData?.request?.employee]);

  if (loading || clientSettingsLoading) return <LoadingPage />;
  if (!clientSettingsData.request) {
    return <Redirect to="/employee_error?reason=session_expired" />;
  }
  const {
    request: { employee, requestComments }
  } = clientSettingsData;
  if (!['in_progress', 'rejected'].includes(employee.status)) {
    return <Redirect to="/employee_finished" />;
  }
  const submissionDeadlineAt = clientSettingsData.clientSetting?.submissionDeadline?.submissionDeadlineAt;
  if ('in_progress' === employee.status && new Date(submissionDeadlineAt) < new Date()) {
    return <Redirect to="/employee_expired" />;
  }

  const questions = _.flatMap(data.questionGroups, 'questions');
  const notFinished = questions.filter(question => question.status === 'not_answered');
  const questionMap = _.keyBy(questions, 'code');
  const firstNotAnswered = _.head(notFinished);
  const currentQuestion = current && questionMap[current];
  const currentGroup = data.questionGroups.find(group => group.questions.some(question => question.id === currentQuestion?.id));
  const progress = _.round(((questions.length - notFinished.length) / questions.length) * 100);
  const currentTitle = currentQuestion
    ? `STEP ${currentGroup.id} - ${currentGroup.questions.findIndex(question => question.id === currentQuestion.id) + 1}  ${
        currentQuestion.name
      }`
    : '年末調整終了';
  let Question = null;
  let Finish = questionComponents.finish;

  if (!currentQuestion || currentQuestion.status === 'skipped' || (firstNotAnswered && currentQuestion.no > firstNotAnswered.no)) {
    if (firstNotAnswered) {
      return <Redirect to={`/employee_input?current=${firstNotAnswered.code}`} />;
    } else {
      Question = () => <Finish isAdjTarget={questionMap.basic_infos.status === 'answered'} />;
    }
  }

  if (!Question) {
    switch (currentQuestion.code) {
      case 'spouse':
        Question = () => <Spouse question={questionMap['spouse']} />;
        break;
      case 'dependents':
        Question = () => <OtherFamilies question={questionMap['dependents']} />;
        break;
      case 'life_insurances':
        Question = () => <LifeInsurances question={questionMap['life_insurances']} />;
        break;
      default: {
        const QuestionComponent = questionComponents[_.camelCase(currentQuestion.code)];
        Question = () => <QuestionComponent questionMap={questionMap} />;
        break;
      }
    }
  }

  return (
    <div>
      <div className="l-main-title-wrap">
        <h1 className="m-title-main">年末調整</h1>
        <p className="m-title-main-note">1画面ごとに自動保存されており入力途中から再開が可能です</p>
        {submissionDeadlineAt && (
          <p className="m-title-main-note">
            <span className={styles.notice}>提出期日: {moment(submissionDeadlineAt).format('YYYY年M月D日 H時')}</span>
          </p>
        )}
      </div>

      <div className="l-contents-wrap">
        <div className="l-wrap-xxl">
          <div className={styles.progress}>
            <div className={styles.progressCount}>
              <span className={styles.progressTitle}>{currentTitle}</span>
              {questions.length - notFinished.length}/{questions.length}
            </div>
            <Progress progress={progress} key="progress" className={styles.progressBar} large />
          </div>
          {requestComments?.length > 0 && (
            <div className="l-flex">
              <div className={styles.commentBoxList}>
                <ListGroup>
                  <ListGroup.Title>コメント</ListGroup.Title>
                  {requestComments
                    .filter(comment => !!comment.comment)
                    .map(comment => {
                      const CommentIcon = comment.commentBy === 'admin' ? AdminUser : SingleEmployee;
                      return (
                        <ListGroup.Item key={comment.id}>
                          <div>
                            <p>
                              <CommentIcon className={styles.commentIcon} />
                              {comment.commentBy === 'admin' ? (
                                <span>{`管理者(${comment.actedUser ? comment.actedUser.name : '在籍していない管理者'})`}</span>
                              ) : (
                                <span>{`${employee.profile.lastName} ${employee.profile.firstName}`}</span>
                              )}
                              <span className={styles.date}>{recordDisplay.dateOrTime(comment.createdAt)}</span>
                            </p>
                            <p className={styles.comment}>{comment.comment}</p>
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                </ListGroup>
              </div>
            </div>
          )}
          <div className={styles.wrap}>
            <div className={styles.main}>
              <QuestionDescriptionsProvider descriptions={currentQuestion?.questionDescriptions}>
                <Question />
              </QuestionDescriptionsProvider>
            </div>
            <div className={styles.aside}>
              {data.questionGroups.map(questionGroup => (
                <QuestionGroup
                  key={questionGroup.id}
                  isCurrentGroup={questionGroup.id === currentGroup?.id}
                  {...{ questionGroup, firstNotAnswered, currentQuestion }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeInput
  |> lifecycle({
    componentDidUpdate({ current }) {
      if (this.props.current !== current) {
        window.scrollTo(0, 0);
      }
    }
  })
  |> withProps(({ location: { search } }) => ({ current: parse(search).current }));
